import { AgFinancing, AgOrderInfo } from '@indigo-ag/schema';

import i18next from 'core/i18n';
import { FinancingProcessUtil, brazil, date, text as textUtils } from 'shared/utils';

export interface FormattedAgFinanceRecord {
  id: string;
  name: string;
  stage: string;
  type: string;
  state: string;
  cpfOrCnpj: string;
  campaign: string;
  opportunities?: AgOrderInfo['opportunities'];
  phase2?: AgFinancing['phase2'];
  date: AgFinancing['date'];
  updatedAt: AgFinancing['updatedAt'];
  prioritySince?: string;
  hasActiveConversations: AgFinancing['hasActiveConversations'];
  matriculaCount?: number;
  kmlCount?: number;
  collateralType: AgFinancing['collateralType'];
  creditAnalystAssigned?: AgFinancing['creditAnalystAssigned'];
}
export enum CREDIT_ANALYST_DEFAULT_VALUES {
  ASSIGNED = 'assigned',
  UNASSIGNED = 'unassigned'
}
export const AGFCreditAnalystAssignedFilterKeys = new Set([
  CREDIT_ANALYST_DEFAULT_VALUES.UNASSIGNED,
  CREDIT_ANALYST_DEFAULT_VALUES.ASSIGNED
]);

export const calculateStages = (phase2: AgFinancing['phase2'] = {}) => ({
  [textUtils.toTitleCase(
    i18next.t('collateralAnalysis')
  )]: FinancingProcessUtil.phase2.isPhase2CollateralAnalysisComplete(phase2),
  [textUtils.toTitleCase(i18next.t('creditAnalysis'))]: FinancingProcessUtil.phase2.isPhase2CreditComplete(phase2),

  // Only display terramagna tag if started.
  [!!phase2?.fieldMonitoring?.startedAt
    ? textUtils.firstLetterUppercase(i18next.t('georeferencing'))
    : '']: FinancingProcessUtil.phase2.isPhase2FieldMonitoringComplete(phase2)
});

export const formatAgFinanceQueue = (nodes: AgFinancing[]): FormattedAgFinanceRecord[] => {
  return nodes.map(financing => {
    const grower = financing.grower;
    const cleanCpfCnpj = grower?.cpfOrCnpj ? brazil.cleanCpfCnpjValue(grower.cpfOrCnpj) : '-';
    const stage = FinancingProcessUtil.getCurrentStage(financing);

    return {
      id: financing.id || ' - ',
      name: grower?.name || ' - ',
      stage,
      type: grower?.leadType || ' - ',
      state: grower?.state ? grower.state.name : ' - ',
      cpfOrCnpj: cleanCpfCnpj?.length === 14 ? brazil.cnpjInputMask(cleanCpfCnpj) : brazil.cpfInputMask(cleanCpfCnpj),
      campaign: financing.campaign?.name || ' - ',
      opportunities: financing.orderInfo?.opportunities,
      phase2: financing.phase2,
      date: financing.date,
      updatedAt: financing.updatedAt,
      prioritySince: financing.prioritySince ? date.fromNow(financing.prioritySince) : undefined,
      hasActiveConversations: financing.hasActiveConversations || false,
      matriculaCount: financing.phase2?.matriculas?.length || 0,
      kmlCount: financing.phase2?.matriculas?.filter(m => !!m.kml?.id).length || 0,
      collateralType: financing.collateralType,
      creditAnalystAssigned: financing.creditAnalystAssigned
    };
  });
};

export enum STEP_DECISION_REASON_CODE {
  // Reason codes for the decisions that were made passing or failing a background
  // check step automatically.
  PASSED_NOTHING_PROBLEMATIC_FOUND = 'passed/nothing_problematic_found',
  FAILED_FORCED_LABOR_VIOLATION = 'failed/forced_labor_violation',
  FAILED_OUTSTANDING_DEBTS = 'failed/outstanding_debts',
  FAILED_ENVIRONMENTAL_EMBARGO = 'failed/environmental_embargo'
}
